import type { BulleEnrichie, Decompte } from '../partage/api/types';
import { intertitresDeLaListe, type CleDIntertitre } from './intertitresDeLaListe';
import { selonMonOrdre } from './monOrdre';
import {
  avantLe,
  auMilieu,
  LIGNE_ATTEND,
  LIGNE_FAITE,
  LIGNE_IDEE,
  proprietesEnMots,
  reponseAttendueIlYA,
  surN,
  type Rangement,
} from '../partage/vocabulaire';

/**
 * La Liste rangée (chantier `revue-design`, ticket 03, `design/liste.html`, décisions d'Igor du
 * 2026-10-03, « La Liste » 1 et 3). Sans rendu, testé dans `tests/client/projets/rangerLaListe.test.ts`.
 *
 * « Ranger » a trois choix, une fonction pure par choix :
 *   · **Dans mon ordre** (par défaut) : ton rang d'abord, puis l'ordre d'arrivée — l'ordre que
 *     le serveur rend avec chaque feuille (`ordreHierarchique`), lu tel quel, pour la feuille
 *     comme pour les étapes dépliées (chantier nettoyage-feuille, ticket 04 : plus recalculé ici) ;
 *   · **Par importance** : les intertitres d'aujourd'hui (`intertitresDeLaListe.ts`) ;
 *   · **Par date** : la date la plus proche de la bulle (son échéance ou son rendez-vous), puis
 *     ce qui n'a pas de date, dans ton ordre.
 *
 * `ordre` : l'`ordreHierarchique` de la feuille d'où viennent les bulles.
 *
 * Ce qu'une ligne dit d'elle-même, à droite (`ceQueDitLaLigne`), est ici aussi : des mots tirés
 * de la bulle. Le « N sur M » d'une étape, lui, vient du serveur avec la feuille (`decompte`,
 * compté par `progression`).
 */

type BulleDOrdre = Pick<BulleEnrichie, 'id'>;
type BulleDatee = BulleDOrdre & Pick<BulleEnrichie, 'dateEcheance' | 'debut'>;
type BulleDeListe = BulleDatee & Pick<BulleEnrichie, 'etat' | 'priorite'>;

export function rangerDansMonOrdre<B extends BulleDOrdre>(bulles: readonly B[], ordre: readonly string[]): B[] {
  return [...bulles].sort(selonMonOrdre(ordre));
}

/**
 * Le jour qui range une bulle « Par date » : le plus proche de son échéance et de son
 * rendez-vous (le jour du rendez-vous, sans l'heure). `null` : pas de date.
 */
export function jourDeRangement(bulle: Pick<BulleEnrichie, 'dateEcheance' | 'debut'>): string | null {
  const jours = [bulle.dateEcheance, bulle.debut?.slice(0, 10) ?? null].filter(
    (j): j is string => !!j
  );
  return jours.length > 0 ? jours.sort()[0]! : null;
}

export function rangerParDate<B extends BulleDatee>(bulles: readonly B[], ordre: readonly string[]): B[] {
  const parMonOrdre = selonMonOrdre(ordre);
  return [...bulles].sort((a, b) => {
    const ja = jourDeRangement(a);
    const jb = jourDeRangement(b);
    if (ja !== jb) {
      if (ja === null) return 1;
      if (jb === null) return -1;
      return ja < jb ? -1 : 1;
    }
    return parMonOrdre(a, b);
  });
}

/** Ce qui se range sous un même intertitre ; sans intertitre (`nom` nul) hors « Par importance ». */
export interface GroupeDeLaListe<B> {
  cle: CleDIntertitre | 'tout';
  nom: string | null;
  glose: string | null;
  bulles: B[];
}

export function groupesDeLaListe<B extends BulleDeListe>(
  bulles: readonly B[],
  rangement: Rangement,
  ordre: readonly string[]
): GroupeDeLaListe<B>[] {
  if (bulles.length === 0) return [];
  if (rangement === 'importance') {
    return intertitresDeLaListe(bulles, ordre).map(({ cle, nom, glose, bulles: dedans }) => ({
      cle,
      nom,
      glose,
      bulles: dedans,
    }));
  }
  return [{ cle: 'tout', nom: null, glose: null, bulles: rangerLesTaches(bulles, rangement, ordre) }];
}

/**
 * Les tâches dépliées sous une étape : dans l'ordre de sa feuille, ou par date si la Liste est
 * rangée par date. « Par importance » n'a d'intertitres qu'au premier niveau ; dessous, ton ordre.
 */
export function rangerLesTaches<B extends BulleDatee>(
  bulles: readonly B[],
  rangement: Rangement,
  ordre: readonly string[]
): B[] {
  return rangement === 'date' ? rangerParDate(bulles, ordre) : rangerDansMonOrdre(bulles, ordre);
}

type BulleQuiParle = Pick<
  BulleEnrichie,
  'etat' | 'nbEnfants' | 'effortMin' | 'canal' | 'dateEcheance' | 'situation'
>;

/**
 * Ce qu'une ligne dit d'elle-même, à droite (décision 3) : « fait », « idée gardée en
 * réflexion », ou, pour ce qui reste à faire, « 1 sur 3 » (une étape), la durée et le lieu (une
 * tâche), « attend une réponse », « avant le 31 octobre 2026 ». `monde` : la réponse se fait
 * attendre, la ligne le dit en ambre (jamais en rouge).
 */
export function ceQueDitLaLigne(
  bulle: BulleQuiParle,
  decompte: Decompte | null
): { texte: string; monde: boolean } {
  if (bulle.etat === 'fait') return { texte: LIGNE_FAITE, monde: false };
  if (bulle.etat === 'sans_objet') return { texte: LIGNE_IDEE, monde: false };

  const morceaux: string[] = [];
  if (bulle.nbEnfants > 0) {
    if (decompte) morceaux.push(surN(decompte.faites, decompte.total));
  } else {
    morceaux.push(...proprietesEnMots(bulle, ['duree', 'lieu']).map(auMilieu));
  }
  const relance = bulle.situation.nom === 'a_verifier';
  if (relance) morceaux.push(reponseAttendueIlYA(bulle.situation.joursDAttente));
  else if (bulle.etat === 'en_attente_confirmation') morceaux.push(LIGNE_ATTEND);
  if (bulle.dateEcheance) morceaux.push(avantLe(bulle.dateEcheance));
  return { texte: morceaux.join(' · '), monde: relance };
}
